import { useCallback, useEffect, useMemo } from "react";
import type { CmmdCampaign, CmmdProject, CmmdTask, CmmdTaskStatus } from "@cmmd-forge/contracts";

import type { Project } from "../types";
import type { WorkItem } from "./homebase/unifiedWorkItems";
import type { TaskDetailThreadSummary } from "./command-center/TaskDetailPanel";
import {
  applyCommandCenterFilters,
  buildAssigneeByWorkItemId,
  buildCmmdTaskByWorkItemId,
  buildCmmdTaskMetaByWorkItemId,
  buildCmmdTaskTitleById,
  buildCommandCenterAssigneeOptions,
  buildCommandCenterCampaignOptions,
  buildCommandCenterProjectOptions,
  buildCommandCenterTeamOptions,
  buildCommandCenterWorkspaceOptions,
  buildProjectNameByScopedId,
  buildSelectedTaskThreadSummaries,
  hasActiveCommandCenterFilters,
  resolveCommandCenterEmptyStateContent,
  scopeCommandCenterItemsByCampaign,
  scopeCommandCenterItemsByProject,
  scopeCommandCenterItemsByTeam,
  scopeCommandCenterItemsByWorkspace,
  scopeCommandCenterProjectsByCampaign,
  scopeCommandCenterProjectsByTeam,
  scopeCommandCenterProjectsByWorkspace,
  scopeCommandCenterTasksByCampaign,
  scopeCommandCenterTasksByProject,
  scopeCommandCenterTasksByTeam,
  scopeCommandCenterTasksByWorkspace,
  type CommandCenterOption,
  type ManualCommandTask,
} from "./NoActiveThreadState.logic";

interface EnvironmentThreadState {
  readonly sidebarThreadSummaryById: Readonly<
    Record<
      string,
      Parameters<
        typeof buildSelectedTaskThreadSummaries
      >[0]["environmentStateById"][string]["sidebarThreadSummaryById"][string]
    >
  >;
}

interface WorkspaceSnapshot {
  readonly id: string;
  readonly name: string;
}

export interface CommandCenterFilterState {
  readonly activeWorkspaceId: string | null;
  readonly setActiveWorkspaceId: (workspaceId: string | null) => void;
  readonly readyOnly: boolean;
  readonly statusFilter: CmmdTaskStatus | "all";
  readonly setStatusFilter: (status: CmmdTaskStatus | "all") => void;
  readonly teamFilter: string;
  readonly setTeamFilter: (value: string) => void;
  readonly campaignFilter: string;
  readonly setCampaignFilter: (value: string) => void;
  readonly projectFilter: string;
  readonly setProjectFilter: (value: string) => void;
  readonly assigneeFilter: string;
  readonly setAssigneeFilter: (value: string) => void;
  readonly searchQuery: string;
}

export interface UseNoActiveThreadDerivedStateInput {
  readonly projects: readonly Project[];
  readonly workspace: WorkspaceSnapshot | null;
  readonly cmmdCampaigns: readonly CmmdCampaign[];
  readonly cmmdProjects: readonly CmmdProject[];
  readonly cmmdTasks: readonly CmmdTask[];
  readonly cmmdWorkspaces: readonly WorkspaceSnapshot[];
  readonly manualTasks: readonly ManualCommandTask[];
  readonly unifiedFlatItems: readonly WorkItem[];
  readonly filters: CommandCenterFilterState;
  readonly selectedItemId: string | null;
  readonly environmentStateById: Readonly<Record<string, EnvironmentThreadState>>;
  readonly hasCmmdContext: boolean;
}

export function useNoActiveThreadDerivedState(input: UseNoActiveThreadDerivedStateInput) {
  const base = useCommandCenterBaseState(input);
  const scoped = useCommandCenterScopedState(input, base);
  const selection = useCommandCenterSelectionState(input, base, scoped);
  const activeFilters = hasActiveCommandCenterFilters({
    activeWorkspaceId: input.filters.activeWorkspaceId,
    teamFilter: input.filters.teamFilter,
    campaignFilter: input.filters.campaignFilter,
    projectFilter: input.filters.projectFilter,
    statusFilter: input.filters.statusFilter,
    assigneeFilter: input.filters.assigneeFilter,
    searchQuery: input.filters.searchQuery,
    readyOnly: input.filters.readyOnly,
  });
  const activeWorkspaceName =
    base.workspaces.find((item) => item.id === input.filters.activeWorkspaceId)?.name ?? null;
  const emptyStateContent = resolveCommandCenterEmptyStateContent({
    hasActiveFilters: activeFilters,
    activeWorkspaceName,
    hasCmmdContext: input.hasCmmdContext,
  });

  return {
    ...base,
    ...scoped,
    ...selection,
    hasActiveFilters: activeFilters,
    emptyStateContent,
  };
}

function useCommandCenterBaseState(input: UseNoActiveThreadDerivedStateInput) {
  const workspaces = useMemo(
    () =>
      buildCommandCenterWorkspaceOptions({
        workspaces: input.cmmdWorkspaces,
        fallbackWorkspace: input.workspace,
      }),
    [input.cmmdWorkspaces, input.workspace],
  );
  useEffect(() => {
    if (input.filters.activeWorkspaceId === null) return;
    if (workspaces.some((item) => item.id === input.filters.activeWorkspaceId)) return;
    input.filters.setActiveWorkspaceId(null);
  }, [input.filters, workspaces]);

  const projectNameByScopedId = useMemo(
    () => buildProjectNameByScopedId(input.projects),
    [input.projects],
  );
  const manualWorkItems = useMemo<readonly WorkItem[]>(
    () =>
      input.manualTasks.map((task) => ({
        id: task.id,
        title: task.title,
        source: "cmmd-task",
        status: task.status,
        priority: "medium",
        projectName:
          task.projectName ??
          (task.projectEnvironmentId && task.projectId
            ? projectNameByScopedId.get(`${task.projectEnvironmentId}:${task.projectId}`)
            : null) ??
          "Manual",
        campaignName: null,
        workspaceId: null,
        workspaceName: null,
        teamId: null,
        teamName: null,
        assigneeId: null,
        dueDate: null,
        agentStatus: "none",
        agentProvider: null,
        environmentId: task.projectEnvironmentId,
        threadId: null,
        projectId: task.projectId,
        activityAt: task.updatedAt,
        hasActionableProposedPlan: false,
        needsAttention: false,
        dependsOn: [],
        blockedByCount: 0,
        isReady: task.status === "todo",
      })),
    [input.manualTasks, projectNameByScopedId],
  );
  const allItems = useMemo(
    () => [
      ...manualWorkItems,
      ...input.unifiedFlatItems.filter((item) => item.source !== "local-thread"),
    ],
    [manualWorkItems, input.unifiedFlatItems],
  );

  return {
    workspaces,
    allItems,
    workItemById: useMapById(allItems),
    cmmdCampaignById: useMapById(input.cmmdCampaigns),
    cmmdProjectById: useMapById(input.cmmdProjects),
    cmmdTaskById: useMapById(input.cmmdTasks),
    cmmdTaskByWorkItemId: useMemo(
      () => buildCmmdTaskByWorkItemId(input.cmmdTasks),
      [input.cmmdTasks],
    ),
    assigneeByWorkItemId: useMemo(
      () => buildAssigneeByWorkItemId(input.cmmdTasks),
      [input.cmmdTasks],
    ),
    cmmdTaskTitleById: useMemo(() => buildCmmdTaskTitleById(input.cmmdTasks), [input.cmmdTasks]),
  };
}

function useCommandCenterScopedState(
  input: UseNoActiveThreadDerivedStateInput,
  base: ReturnType<typeof useCommandCenterBaseState>,
) {
  const cmmdTaskMetaByWorkItemId = useMemo(
    () =>
      buildCmmdTaskMetaByWorkItemId({
        tasks: input.cmmdTasks,
        projects: input.cmmdProjects,
        campaigns: input.cmmdCampaigns,
        projectById: base.cmmdProjectById,
      }),
    [base.cmmdProjectById, input.cmmdCampaigns, input.cmmdProjects, input.cmmdTasks],
  );
  const workspaceScopedItems = useMemo(
    () =>
      scopeCommandCenterItemsByWorkspace({
        items: base.allItems,
        activeWorkspaceId: input.filters.activeWorkspaceId,
        taskMetaByWorkItemId: cmmdTaskMetaByWorkItemId,
      }),
    [base.allItems, cmmdTaskMetaByWorkItemId, input.filters.activeWorkspaceId],
  );
  const workspaceScopedCmmdTasks = useMemo(
    () =>
      scopeCommandCenterTasksByWorkspace({
        tasks: input.cmmdTasks,
        activeWorkspaceId: input.filters.activeWorkspaceId,
        taskMetaByWorkItemId: cmmdTaskMetaByWorkItemId,
      }),
    [cmmdTaskMetaByWorkItemId, input.cmmdTasks, input.filters.activeWorkspaceId],
  );
  const workspaceScopedCmmdProjects = useMemo(
    () =>
      scopeCommandCenterProjectsByWorkspace({
        projects: input.cmmdProjects,
        activeWorkspaceId: input.filters.activeWorkspaceId,
      }),
    [input.cmmdProjects, input.filters.activeWorkspaceId],
  );
  return useFilteredCommandCenterState(input, base, {
    cmmdTaskMetaByWorkItemId,
    workspaceScopedItems,
    workspaceScopedCmmdTasks,
    workspaceScopedCmmdProjects,
  });
}

function useFilteredCommandCenterState(
  input: UseNoActiveThreadDerivedStateInput,
  base: ReturnType<typeof useCommandCenterBaseState>,
  scoped: {
    readonly cmmdTaskMetaByWorkItemId: ReturnType<typeof buildCmmdTaskMetaByWorkItemId>;
    readonly workspaceScopedItems: readonly WorkItem[];
    readonly workspaceScopedCmmdTasks: readonly CmmdTask[];
    readonly workspaceScopedCmmdProjects: readonly CmmdProject[];
  },
) {
  const teamScopedItems = useMemo(
    () =>
      scopeCommandCenterItemsByTeam({
        items: scoped.workspaceScopedItems,
        teamFilter: input.filters.teamFilter,
        taskMetaByWorkItemId: scoped.cmmdTaskMetaByWorkItemId,
      }),
    [input.filters.teamFilter, scoped.cmmdTaskMetaByWorkItemId, scoped.workspaceScopedItems],
  );
  const teamScopedCmmdTasks = useMemo(
    () =>
      scopeCommandCenterTasksByTeam({
        tasks: scoped.workspaceScopedCmmdTasks,
        teamFilter: input.filters.teamFilter,
        taskMetaByWorkItemId: scoped.cmmdTaskMetaByWorkItemId,
      }),
    [input.filters.teamFilter, scoped.cmmdTaskMetaByWorkItemId, scoped.workspaceScopedCmmdTasks],
  );
  const teamScopedCmmdProjects = useMemo(
    () =>
      scopeCommandCenterProjectsByTeam({
        projects: scoped.workspaceScopedCmmdProjects,
        teamFilter: input.filters.teamFilter,
      }),
    [input.filters.teamFilter, scoped.workspaceScopedCmmdProjects],
  );
  const teamOptions = useMemo(
    () =>
      buildCommandCenterTeamOptions({
        tasks: scoped.workspaceScopedCmmdTasks,
        projects: scoped.workspaceScopedCmmdProjects,
        taskMetaByWorkItemId: scoped.cmmdTaskMetaByWorkItemId,
      }),
    [
      scoped.cmmdTaskMetaByWorkItemId,
      scoped.workspaceScopedCmmdProjects,
      scoped.workspaceScopedCmmdTasks,
    ],
  );
  useValidOption(input.filters.teamFilter, teamOptions, input.filters.setTeamFilter);

  const campaignOptions = useMemo(
    () =>
      buildCommandCenterCampaignOptions({
        items: teamScopedItems,
        tasks: teamScopedCmmdTasks,
        projects: teamScopedCmmdProjects,
        campaignsById: base.cmmdCampaignById,
        taskMetaByWorkItemId: scoped.cmmdTaskMetaByWorkItemId,
      }),
    [
      base.cmmdCampaignById,
      scoped.cmmdTaskMetaByWorkItemId,
      teamScopedCmmdProjects,
      teamScopedCmmdTasks,
      teamScopedItems,
    ],
  );
  useValidOption(input.filters.campaignFilter, campaignOptions, input.filters.setCampaignFilter);

  const campaignScopedItems = useMemo(
    () =>
      scopeCommandCenterItemsByCampaign({
        items: teamScopedItems,
        campaignFilter: input.filters.campaignFilter,
        taskMetaByWorkItemId: scoped.cmmdTaskMetaByWorkItemId,
      }),
    [input.filters.campaignFilter, scoped.cmmdTaskMetaByWorkItemId, teamScopedItems],
  );
  const campaignScopedCmmdTasks = useMemo(
    () =>
      scopeCommandCenterTasksByCampaign({
        tasks: teamScopedCmmdTasks,
        campaignFilter: input.filters.campaignFilter,
        taskMetaByWorkItemId: scoped.cmmdTaskMetaByWorkItemId,
      }),
    [input.filters.campaignFilter, scoped.cmmdTaskMetaByWorkItemId, teamScopedCmmdTasks],
  );
  const campaignScopedCmmdProjects = useMemo(
    () =>
      scopeCommandCenterProjectsByCampaign({
        projects: teamScopedCmmdProjects,
        campaignFilter: input.filters.campaignFilter,
      }),
    [input.filters.campaignFilter, teamScopedCmmdProjects],
  );
  const projectOptions = useMemo(
    () =>
      buildCommandCenterProjectOptions({
        items: campaignScopedItems,
        tasks: campaignScopedCmmdTasks,
        projects: campaignScopedCmmdProjects,
      }),
    [campaignScopedCmmdProjects, campaignScopedCmmdTasks, campaignScopedItems],
  );
  useValidOption(input.filters.projectFilter, projectOptions, input.filters.setProjectFilter);

  const projectScopedItems = useMemo(
    () =>
      scopeCommandCenterItemsByProject({
        items: campaignScopedItems,
        projectFilter: input.filters.projectFilter,
        taskMetaByWorkItemId: scoped.cmmdTaskMetaByWorkItemId,
      }),
    [campaignScopedItems, input.filters.projectFilter, scoped.cmmdTaskMetaByWorkItemId],
  );
  const projectScopedCmmdTasks = useMemo(
    () =>
      scopeCommandCenterTasksByProject({
        tasks: campaignScopedCmmdTasks,
        projectFilter: input.filters.projectFilter,
        taskMetaByWorkItemId: scoped.cmmdTaskMetaByWorkItemId,
      }),
    [campaignScopedCmmdTasks, input.filters.projectFilter, scoped.cmmdTaskMetaByWorkItemId],
  );
  const assigneeOptions = useMemo(
    () => buildCommandCenterAssigneeOptions({ tasks: projectScopedCmmdTasks }),
    [projectScopedCmmdTasks],
  );
  useValidOption(input.filters.assigneeFilter, assigneeOptions, input.filters.setAssigneeFilter);
  const assigneeLabelById = useOptionLabelMap(assigneeOptions);
  const filteredItems = useMemo(
    () =>
      applyCommandCenterFilters({
        items: base.allItems,
        filters: {
          activeWorkspaceId: input.filters.activeWorkspaceId,
          searchQuery: input.filters.searchQuery,
          teamFilter: input.filters.teamFilter,
          campaignFilter: input.filters.campaignFilter,
          projectFilter: input.filters.projectFilter,
          statusFilter: input.filters.statusFilter,
          assigneeFilter: input.filters.assigneeFilter,
          readyOnly: input.filters.readyOnly,
        },
        taskMetaByWorkItemId: scoped.cmmdTaskMetaByWorkItemId,
        assigneeByWorkItemId: base.assigneeByWorkItemId,
        assigneeLabelById,
      }),
    [
      assigneeLabelById,
      base.allItems,
      base.assigneeByWorkItemId,
      input.filters,
      scoped.cmmdTaskMetaByWorkItemId,
    ],
  );

  return {
    cmmdTaskMetaByWorkItemId: scoped.cmmdTaskMetaByWorkItemId,
    teamOptions,
    campaignOptions,
    projectOptions,
    assigneeOptions,
    projectScopedItems,
    filteredItems,
    readyCount: projectScopedItems.filter((item) => item.isReady).length,
  };
}

function useCommandCenterSelectionState(
  input: UseNoActiveThreadDerivedStateInput,
  base: ReturnType<typeof useCommandCenterBaseState>,
  scoped: ReturnType<typeof useCommandCenterScopedState>,
) {
  const selectedItem = useMemo(() => {
    const item =
      scoped.filteredItems.find((candidate) => candidate.id === input.selectedItemId) ?? null;
    return item?.source === "cmmd-task" ? item : null;
  }, [input.selectedItemId, scoped.filteredItems]);
  const selectedTaskThreads = useMemo<readonly TaskDetailThreadSummary[]>(
    () =>
      buildSelectedTaskThreadSummaries({
        selectedItemId: selectedItem?.id ?? null,
        environmentStateById: input.environmentStateById,
      }),
    [input.environmentStateById, selectedItem?.id],
  );
  const selectedAssigneeId = useMemo(() => {
    if (!selectedItem || !selectedItem.id.startsWith("cmmd:")) return null;
    return base.cmmdTaskByWorkItemId.get(selectedItem.id)?.assigneeId ?? null;
  }, [base.cmmdTaskByWorkItemId, selectedItem]);
  const dependencyLookup = useMapById(base.allItems);
  const resolveDependency = useCallback(
    (id: string) => {
      const hit = dependencyLookup.get(id);
      if (!hit) return null;
      const done = hit.status === "done" || hit.status === "completed" || hit.status === "closed";
      return { id: hit.id, title: hit.title, status: hit.status, done };
    },
    [dependencyLookup],
  );

  return {
    selectedItem,
    selectedTaskThreads,
    selectedAssigneeId,
    resolveDependency,
    selectedDescription: null as string | null,
  };
}

function useMapById<T extends { readonly id: string }>(
  items: readonly T[],
): ReadonlyMap<string, T> {
  return useMemo(() => {
    const map = new Map<string, T>();
    for (const item of items) map.set(item.id, item);
    return map;
  }, [items]);
}

function useValidOption(
  value: string,
  options: ReadonlyArray<CommandCenterOption>,
  setValue: (value: string) => void,
): void {
  useEffect(() => {
    if (options.some((option) => option.value === value)) return;
    setValue("all");
  }, [options, setValue, value]);
}

function useOptionLabelMap(
  options: ReadonlyArray<CommandCenterOption>,
): ReadonlyMap<string, string> {
  return useMemo(() => {
    const map = new Map<string, string>();
    for (const option of options) {
      if (option.value === "all" || option.value === "unassigned") continue;
      map.set(option.value, option.label);
    }
    return map;
  }, [options]);
}
